import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useReactFlow } from '@xyflow/react';
import { useMemo, useState } from 'react';
import { CloseIon } from 'src/components/icons/CloseIcon';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { twMerge } from 'tailwind-merge';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../../models/node-data';
import { AutoTab } from './auto-tab';
import { StandaloneTab } from './standalone-tab';

const tabs = [
  { name: 'Stand-alone cfg files', key: 'standalone' },
  { name: 'Auto Configuration Deployment bundle', key: 'auto' },
];

export default function DialogPackage() {
  const [currentTab, setCurrentTab] = useState<string>(tabs[0].key);
  const { setShowDialogs } = useGlobalContext();
  const [wizardExport, setWizardExport] = useState<boolean>(false);

  const { getNodes } = useReactFlow();
  const nodes = getNodes();

  const allNodeSwitches = useMemo(
    () =>
      nodes.filter(
        (node) =>
          (node.data?.category as NodeCategoryModel)?.type ===
            EDataType.Switch &&
          !(node.data?.category as NodeCategoryModel)?.switchType?.includes(
            EDataSwitchType.UNMANAGED,
          ),
      ),
    [nodes],
  );

  const onCancel = () => {
    setShowDialogs({ package: false });
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-package"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:p-6',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              wizardExport ? 'max-w-7xl' : 'max-w-3xl',
              'flex flex-col gap-1',
            ])}
            data-testid="dialog-package-panel"
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold"
              data-testid="dialog-package-title"
            >
              Export configuration files
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-package-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>
            <div className="relative border-b border-blue-400 pb-0.5">
              <nav
                aria-label="Tabs"
                className="-mb-px flex space-x-4 overflow-x-auto"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.name}
                    type="button"
                    aria-current={currentTab === tab.key ? 'page' : undefined}
                    className={twMerge([
                      'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium',
                      currentTab === tab.key
                        ? 'border-blue-400 bg-blue-400 text-white'
                        : 'border-transparent text-gray-500',
                    ])}
                    onClick={() => setCurrentTab(tab.key)}
                    data-testid={`dialog-package-tab-${tab.key}`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
            {currentTab === 'standalone' && (
              <StandaloneTab allNodeSwitches={allNodeSwitches} />
            )}
            {currentTab === 'auto' && (
              <AutoTab
                allNodeSwitches={allNodeSwitches}
                wizardExport={wizardExport}
                setWizardExport={setWizardExport}
              />
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
